import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  uniqueIndex,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  boolean,
  uuid,
  primaryKey
} from "drizzle-orm/pg-core";
import { qboEntityTypeEnum, qboSyncStatusEnum } from './enums';
import { organizations } from './organizations';
import { users } from './users';

// QuickBooks OAuth Tokens table - Company-wide OAuth connection
export const quickbooksTokens = pgTable("quickbooks_tokens", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // QBO Company/Realm Info
  realmId: varchar("realm_id").notNull(), // QBO Company ID
  companyName: varchar("company_name"), // QBO Company Name
  
  // OAuth Tokens (encrypted at rest)
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  tokenExpiry: timestamp("token_expiry").notNull(),
  
  // Token Metadata
  scope: text("scope"), // OAuth scopes granted
  tokenType: varchar("token_type").default("Bearer"),
  
  // Connection Status
  isActive: boolean("is_active").default(true),
  lastSyncAt: timestamp("last_sync_at"),
  lastSyncStatus: qboSyncStatusEnum("last_sync_status"),
  lastSyncError: text("last_sync_error"),
  
  // Who connected the account
  connectedBy: varchar("connected_by").references(() => users.id),
  connectedAt: timestamp("connected_at").defaultNow(),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_quickbooks_tokens_realm_id").on(table.realmId),
  index("idx_quickbooks_tokens_is_active").on(table.isActive),
  uniqueIndex('qbo_tokens_org_realm_unique').on(table.orgId, table.realmId),
]);


// QuickBooks Sync Log table - Track all sync operations
export const quickbooksSyncLog = pgTable("quickbooks_sync_log", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Entity being synced
  entityType: qboEntityTypeEnum("entity_type").notNull(),
  entityId: varchar("entity_id").notNull(), // Local entity ID
  qboEntityId: varchar("qbo_entity_id"), // QBO entity ID (null if creation failed)
  
  // Sync Operation
  operation: varchar("operation").notNull(), // create, update, delete
  status: qboSyncStatusEnum("status").notNull(),
  
  // Request/Response Data for debugging
  requestPayload: jsonb("request_payload"), // What was sent to QBO
  responsePayload: jsonb("response_payload"), // What QBO returned
  errorMessage: text("error_message"),
  errorCode: varchar("error_code"),
  
  // Retry tracking
  retryCount: integer("retry_count").default(0),
  nextRetryAt: timestamp("next_retry_at"),
  
  // Metadata
  syncedBy: varchar("synced_by").references(() => users.id),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_qbo_sync_log_entity").on(table.entityType, table.entityId),
  index("idx_qbo_sync_log_status").on(table.status),
  index("idx_qbo_sync_log_created").on(table.createdAt),
]);


// QuickBooks Account Mappings - Map local accounts to QBO chart of accounts
export const quickbooksAccountMappings = pgTable("quickbooks_account_mappings", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Local account type
  localAccountType: varchar("local_account_type").notNull().unique(), // "commission_expense", "revenue", "carrier_payment", etc.
  localAccountName: varchar("local_account_name").notNull(),
  
  // QBO Account mapping
  qboAccountId: varchar("qbo_account_id"), // QBO Account ID
  qboAccountName: varchar("qbo_account_name"), // QBO Account Name
  qboAccountType: varchar("qbo_account_type"), // QBO AccountType enum value
  
  // Mapping status
  isMapped: boolean("is_mapped").default(false),
  mappedAt: timestamp("mapped_at"),
  mappedBy: varchar("mapped_by").references(() => users.id),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_qbo_account_mappings_local_type").on(table.localAccountType),
  index("idx_qbo_account_mappings_qbo_id").on(table.qboAccountId),
]);
